import { useState, type JSX } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, request, requestBlob } from '../api/client';

interface ClienteRico {
  id: string;
  primeiroNome: string;
  telefone: string;
  email: string | null;
  status: string;
  cohort: string | null;
  dentroJanela: boolean;
  diasDesdeCompra: number | null;
  fezTiragemPaga: boolean;
  qtdTiragens: number;
  ultimaTiragemEm: string | null;
  tiragensNoPeriodo: number;
  temCompraFront: boolean;
  temCompraMapa: boolean;
  acessoMapaManual: boolean;
  temAcessoMapa: boolean;
  ultimaMensagemClienteEm: string | null;
  diasSemInteragir: number | null;
  disparosRecebidos: number;
  ultimoDisparoEm: string | null;
  optOut: boolean;
  createdAt: string;
}
interface ClientePage {
  items: ClienteRico[];
  total: number;
  page: number;
  pageSize: number;
}

/** Formata sempre em UTC-3 (Brasília), independente do fuso do navegador. */
function dtBR(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ClientesPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cohort, setCohort] = useState('');
  const [tiragem, setTiragem] = useState(''); // '' | 'fez' | 'nunca'
  const [optOut, setOptOut] = useState(''); // '' | 'sim' | 'nao'
  const [sort, setSort] = useState<string>('createdAt');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const handleSearch = (value: string): void => {
    setSearch(value);
    setPage(1);
    window.clearTimeout((handleSearch as { _t?: number })._t);
    (handleSearch as { _t?: number })._t = window.setTimeout(() => setDebounced(value), 300);
  };

  const { data, isLoading, isError, refetch } = useQuery<ClientePage>({
    queryKey: ['clientes-rico', debounced, cohort, tiragem, optOut, sort, dir, desde, ate, page],
    queryFn: () => {
      const p = new URLSearchParams();
      if (debounced) p.set('search', debounced);
      if (cohort) p.set('cohort', cohort);
      if (tiragem) p.set('tiragem', tiragem);
      if (optOut) p.set('optOut', optOut);
      if (sort) p.set('sort', sort);
      if (dir) p.set('dir', dir);
      if (desde) p.set('desde', desde);
      if (ate) p.set('ate', ate);
      p.set('page', String(page));
      p.set('pageSize', String(pageSize));
      return request<ClientePage>(`/admin/clientes-rico?${p.toString()}`);
    },
  });

  // Clica no cabeçalho: mesma coluna alterna asc/desc; outra coluna começa desc.
  const toggleSort = (field: string): void => {
    setPage(1);
    if (sort === field) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setDir('desc');
    }
  };
  const seta = (field: string): string => (sort === field ? (dir === 'asc' ? ' ▲' : ' ▼') : '');

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  async function baixarCsv(): Promise<void> {
    setExporting(true);
    setExportErr(null);
    try {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (ate) params.set('ate', ate);
      const q = params.toString();
      const blob = await requestBlob(`/admin/clientes/export${q ? `?${q}` : ''}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contatos-sara${desde || ate ? `_${desde || 'inicio'}_a_${ate || 'hoje'}` : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof ApiError ? e.message : 'Falha ao exportar.');
    } finally {
      setExporting(false);
    }
  }

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // LGPD: apagar os dados pessoais de uma cliente. A rotina já existia no
  // backend desde sempre e não tinha botão nenhum — só dava pra chamar por
  // fora, o que na prática significava "não existe" (achado D10-15).
  //
  // Anonimiza em vez de deletar: nome vira [REMOVIDO], e-mail/telefone/CPF
  // saem, mapa astral e preferências são apagados, e o cadastro fica
  // bloqueado. As compras ficam, por obrigação fiscal.
  const esquecer = useMutation({
    mutationFn: (id: string) => request(`/admin/lgpd/clientes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void refetch();
      alert('Dados pessoais apagados. As compras ficam, por obrigação fiscal.');
    },
    onError: (e: unknown) => alert(`Falha ao apagar: ${e instanceof Error ? e.message : e}`),
  });
  const periodoAtivo = !!(desde || ate);

  return (
    <section aria-labelledby="clientes-heading" className="space-y-4">
      <header>
        <h1 id="clientes-heading" className="text-2xl font-semibold">
          Clientes
        </h1>
        <p className="muted text-sm mt-1">
          Toda a base com os dados que a reativação usa: cohort, tiragens pagas, última tiragem,
          última mensagem da pessoa (UTC-3), dias sem interagir e disparos recebidos. Filtre e
          ordene pra inspecionar tudo na mão.
        </p>
      </header>

      {/* Filtros */}
      <div className="card space-y-3">
        <div className="flex flex-wrap gap-3">
          <label className="block flex-1 min-w-[220px]">
            <span className="sr-only">Buscar</span>
            <input
              type="search"
              placeholder="Buscar por nome, email ou telefone…"
              className="input w-full"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Cohort</span>
            <select
              className="input"
              value={cohort}
              onChange={(e) => {
                setCohort(e.target.value);
                setPage(1);
              }}
            >
              <option value="">todos</option>
              <option value="A">A — em teste</option>
              <option value="B">B — nunca converteu</option>
              <option value="C">C — dormente pagante</option>
            </select>
          </label>
          <label className="block">
            <span className="label">Tiragem paga</span>
            <select
              className="input"
              value={tiragem}
              onChange={(e) => {
                setTiragem(e.target.value);
                setPage(1);
              }}
            >
              <option value="">todos</option>
              <option value="fez">fez tiragem{periodoAtivo ? ' (no período)' : ''}</option>
              <option value="nunca">nunca fez</option>
            </select>
          </label>
          <label className="block">
            <span className="label">Opt-out</span>
            <select
              className="input"
              value={optOut}
              onChange={(e) => {
                setOptOut(e.target.value);
                setPage(1);
              }}
            >
              <option value="">todos</option>
              <option value="nao">recebe disparo</option>
              <option value="sim">desistiu</option>
            </select>
          </label>
          <p className="text-xs text-sara-text-muted self-end">
            clique no cabeçalho da tabela pra ordenar
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-sara-border/40 pt-3">
          <div>
            <span className="label block">Período</span>
            <p className="text-xs text-sara-text-muted mb-1">
              vale pro filtro "fez tiragem no período" e pro CSV
            </p>
            <div className="flex items-end gap-2">
              <label className="block">
                <span className="sr-only">De</span>
                <input
                  type="date"
                  className="input"
                  value={desde}
                  max={ate || undefined}
                  onChange={(e) => {
                    setDesde(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
              <span className="text-sara-text-muted">→</span>
              <label className="block">
                <span className="sr-only">Até</span>
                <input
                  type="date"
                  className="input"
                  value={ate}
                  min={desde || undefined}
                  onChange={(e) => {
                    setAte(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
              {periodoAtivo && (
                <button
                  type="button"
                  className="text-sm text-sara-text-muted underline"
                  onClick={() => {
                    setDesde('');
                    setAte('');
                  }}
                >
                  limpar
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void baixarCsv()}
            disabled={exporting}
          >
            {exporting ? 'Gerando…' : '⬇ Exportar CSV'}
          </button>
        </div>
        {exportErr && (
          <p role="alert" className="text-sara-danger text-sm">
            {exportErr}
          </p>
        )}
      </div>

      <p className="text-sm text-sara-text-muted">
        {isLoading ? 'carregando…' : `${total} cliente(s)`}
      </p>

      {isError && (
        <div className="card text-sm text-sara-danger">
          erro ao carregar.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            tentar de novo
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-sara-text-muted border-b border-sara-border">
              {(
                [
                  ['nome', 'Nome'],
                  ['cohort', 'Cohort'],
                  ['status', 'Status'],
                  ['compras', 'Compras'],
                  ['diasDesdeCompra', 'Dias da compra'],
                  ['qtdTiragens', 'Tiragens pagas'],
                  ['ultimaTiragem', 'Última tiragem'],
                  ['ultimaMensagem', 'Última mensagem'],
                  ['diasSemInteragir', 'Dias parada'],
                  ['disparosRecebidos', 'Disparos'],
                  ['optOut', 'Opt-out'],
                ] as Array<[string, string]>
              ).map(([field, label]) => (
                <th key={field} className="py-2 pr-3">
                  <button
                    type="button"
                    className="font-medium hover:text-sara-text"
                    onClick={() => toggleSort(field)}
                    aria-label={`ordenar por ${label}`}
                  >
                    {label}
                    {seta(field)}
                  </button>
                </th>
              ))}
              <th className="py-2 pr-3 font-medium">Dados</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((c) => (
              <tr key={c.id} className="border-b border-sara-border/40">
                <td className="py-2 pr-3">
                  {c.primeiroNome}
                  <span className="block text-xs text-sara-text-muted">{c.telefone}</span>
                  <span className="block text-xs text-sara-text-muted">
                    {c.email ?? 'sem email'}
                  </span>
                </td>
                <td className="py-2 pr-3">{c.cohort ?? '—'}</td>
                <td className="py-2 pr-3">{c.status}</td>
                <td className="py-2 pr-3">
                  {c.temCompraFront || c.temCompraMapa || c.temAcessoMapa ? (
                    <span className="flex flex-wrap gap-1">
                      {c.temCompraFront && (
                        <span className="inline-block rounded bg-sara-surface px-1.5 py-0.5 text-xs">
                          Front
                        </span>
                      )}
                      {c.temCompraMapa && (
                        <span className="inline-block rounded bg-sara-success/20 px-1.5 py-0.5 text-xs text-sara-success">
                          Mapa ✓ Kiwify
                        </span>
                      )}
                      {!c.temCompraMapa && c.acessoMapaManual && (
                        <span
                          className="inline-block rounded bg-sara-surface px-1.5 py-0.5 text-xs"
                          title="Acesso ao mapa liberado manualmente no painel"
                        >
                          Mapa (manual)
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-sara-text-muted">—</span>
                  )}
                </td>
                <td className="py-2 pr-3">
                  {c.diasDesdeCompra ?? '—'}
                  {c.dentroJanela && (
                    <span className="block text-xs text-sara-success">na janela 7d</span>
                  )}
                </td>
                <td className="py-2 pr-3">
                  {c.qtdTiragens}
                  {c.fezTiragemPaga ? (
                    ''
                  ) : (
                    <span className="block text-xs text-sara-text-muted">nunca</span>
                  )}
                </td>
                <td className="py-2 pr-3">{dtBR(c.ultimaTiragemEm)}</td>
                <td className="py-2 pr-3">{dtBR(c.ultimaMensagemClienteEm)}</td>
                <td className="py-2 pr-3">{c.diasSemInteragir ?? '—'}</td>
                <td className="py-2 pr-3">{c.disparosRecebidos}</td>
                <td className="py-2 pr-3">{c.optOut ? 'Desistiu' : '—'}</td>
                <td className="py-2 pr-3">
                  <button
                    type="button"
                    className="btn-ghost text-xs text-sara-danger"
                    disabled={esquecer.isPending}
                    title="LGPD: apaga nome, e-mail, telefone, CPF, mapa e preferências desta cliente"
                    onClick={() => {
                      // Dupla confirmação: a primeira explica o que some, a
                      // segunda exige digitar. É irreversível e some com o
                      // contato — não pode depender de um clique distraído.
                      if (
                        !window.confirm(
                          `Apagar os dados pessoais de ${c.primeiroNome ?? 'esta cliente'}?\n\n` +
                            'Somem: nome, e-mail, telefone, CPF, mapa astral e preferências.\n' +
                            'Ficam: as compras (obrigação fiscal) e o histórico sem identificação.\n\n' +
                            'NÃO TEM COMO DESFAZER.',
                        )
                      ) {
                        return;
                      }
                      const digitado = window.prompt('Pra confirmar, digite APAGAR');
                      if (digitado?.trim().toUpperCase() !== 'APAGAR') return;
                      esquecer.mutate(c.id);
                    }}
                  >
                    Apagar dados
                  </button>
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={11} className="py-4 text-center text-sara-text-muted">
                  nenhum cliente com esses filtros
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button
            type="button"
            className="px-2 py-1 rounded bg-sara-surface disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← anterior
          </button>
          <span className="text-sara-text-muted">
            página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="px-2 py-1 rounded bg-sara-surface disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            próxima →
          </button>
        </div>
      )}
    </section>
  );
}
